/**
 * AppNav — Phase N3.2 refactor + Phase 4 executive redesign retokening.
 *
 * Slimmed down to a minimal top header + a mobile hamburger button.
 * The prior pill row (Dashboard / Credit Readiness / Credit
 * Improvement / Opportunities) and the persistent horizontal
 * `<ModuleNav />` bar have been REMOVED — both are superseded by
 * `<NavigationSidebar />` which renders as a fixed left rail on
 * desktop (>=1024px) and a modal overlay on mobile.
 *
 * PHASE 4 CHANGE: chrome-only migration from legacy V2 Tailwind utilities
 * to the executive redesign `--ds-*` design-system tokens. Every
 * data-testid, aria-* attribute, keyboard interaction, and behaviour
 * hook (click-outside close, hamburger open, logout redirect) is
 * preserved verbatim.
 *
 * Contract with the rest of the app:
 *   - Every authenticated page still calls `<AppNav />` at the top
 *     of its return tree. This component is 100% drop-in compatible
 *     with the old signature — no page changes needed.
 *   - The sidebar reserves 260px of left padding on desktop via a
 *     `body.nav-tree-active` class + a CSS rule in `index.css`.
 *   - The hamburger button is only visible on mobile (`lg:hidden`).
 *   - Notification bell, theme toggle, and user menu (Profile /
 *     Admin Console / Log out) remain in the header.
 */

import { Menu, LogOut, User as UserIcon, ShieldAlert } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import NavigationSidebar from "@/components/nav/NavigationSidebar";
import NotificationBell from "@/components/notifications/NotificationBell";
import ThemeToggle from "@/components/v2/ThemeToggle";
import SkipToContent from "@/components/a11y/SkipToContent";

function MenuLink({ to, testId, icon: Icon, label, onClick }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      onClick={onClick}
      className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-[14px] text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface-muted)] transition-colors"
      style={{ transitionDuration: "var(--ds-duration-fast)" }}
    >
      {Icon ? <Icon className="w-4 h-4 text-[var(--ds-color-ink-muted)] flex-shrink-0" /> : null}
      <span>{label}</span>
    </Link>
  );
}

export default function AppNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const userMenuRef = useRef(null);
  const hamburgerRef = useRef(null);

  // Click-outside closes the user menu.
  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [userMenuOpen]);

  const onLogout = useCallback(async () => {
    setUserMenuOpen(false);
    try {
      await logout();
    } finally {
      navigate("/login", { replace: true });
    }
  }, [logout, navigate]);

  const displayName =
    user?.profile?.first_name || user?.first_name || user?.email || "Member";

  return (
    <>
      {/* Skip-to-content — WCAG 2.4.1. Rendered first so it lands at the
          top of the keyboard tab order for every authenticated page. */}
      <SkipToContent />
      {/* Fixed left sidebar (desktop) / mobile modal overlay */}
      <NavigationSidebar
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      {/* Slim top header, sits above the page content */}
      <header
        data-testid="app-nav-header"
        className="sticky top-0 z-30 bg-[var(--ds-color-surface)] border-b border-[var(--ds-color-border)]"
      >
        <div className="flex items-center gap-3 px-4 md:px-6 py-3">
          {/* Mobile-only hamburger toggle */}
          <button
            ref={hamburgerRef}
            type="button"
            data-testid="app-nav-hamburger"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation"
            aria-expanded={mobileNavOpen ? "true" : "false"}
            aria-controls="nav-sidebar-mobile-dialog"
            className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-[var(--ds-radius-md)] text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface-muted)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)]"
            style={{ transitionDuration: "var(--ds-duration-fast)" }}
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Brand — visible on mobile only (desktop shows brand in sidebar) */}
          <Link
            to="/dashboard"
            data-testid="app-nav-brand"
            className="lg:hidden font-semibold text-[var(--ds-color-ink)] tracking-tight"
            style={{ fontFamily: "var(--ds-font-display)" }}
          >
            Informed OS
          </Link>

          <div className="flex-1" />

          {/* Notification bell — hidden by its own feature flag internally */}
          <NotificationBell />

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              data-testid="app-nav-user-menu-button"
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen ? "true" : "false"}
              className="inline-flex items-center gap-2 rounded-[var(--ds-radius-pill)] pl-1 pr-3 py-1 hover:bg-[var(--ds-color-surface-muted)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)]"
              style={{ transitionDuration: "var(--ds-duration-fast)" }}
            >
              <span
                aria-hidden="true"
                className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--ds-radius-pill)] bg-[var(--ds-color-accent-soft)] text-[var(--ds-color-accent)] font-semibold text-sm"
              >
                {(displayName || "M")[0].toUpperCase()}
              </span>
              <span className="hidden md:inline text-[14px] text-[var(--ds-color-ink)] max-w-[10rem] truncate">
                {displayName}
              </span>
            </button>
            {userMenuOpen && (
              <div
                data-testid="app-nav-user-menu"
                role="menu"
                className="absolute right-0 mt-2 w-64 rounded-[var(--ds-radius-lg)] overflow-hidden bg-[var(--ds-color-surface)] border border-[var(--ds-color-border)] shadow-[var(--ds-shadow-modal)]"
              >
                <div className="px-4 py-3 border-b border-[var(--ds-color-border)]">
                  <div className="text-[14px] font-semibold text-[var(--ds-color-ink)] truncate">
                    {displayName}
                  </div>
                  <div className="text-[11px] text-[var(--ds-color-ink-muted)] truncate">
                    {user?.email}
                  </div>
                  {user?.role === "admin" && (
                    <span className="inline-block mt-2 text-[11px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-[var(--ds-radius-pill)] bg-[var(--ds-color-accent-soft)] text-[var(--ds-color-accent)] border border-[var(--ds-color-accent)]/30">
                      Admin
                    </span>
                  )}
                </div>
                <MenuLink
                  to="/profile"
                  testId="app-nav-menu-profile"
                  icon={UserIcon}
                  label="Profile"
                  onClick={() => setUserMenuOpen(false)}
                />
                {user?.role === "admin" && (
                  <MenuLink
                    to="/admin"
                    testId="app-nav-menu-admin"
                    icon={ShieldAlert}
                    label="Admin Console"
                    onClick={() => setUserMenuOpen(false)}
                  />
                )}
                <div
                  className="px-4 py-2.5 flex items-center justify-between gap-3 text-[14px] text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface-muted)] transition-colors"
                  data-testid="app-nav-menu-theme-row"
                  style={{ transitionDuration: "var(--ds-duration-fast)" }}
                >
                  <span className="text-[var(--ds-color-ink-muted)]">Theme</span>
                  <ThemeToggle compact />
                </div>
                <button
                  type="button"
                  data-testid="app-nav-menu-logout"
                  onClick={onLogout}
                  className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-[14px] text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface-muted)] transition-colors"
                  style={{ transitionDuration: "var(--ds-duration-fast)" }}
                >
                  <LogOut className="w-4 h-4 text-[var(--ds-color-ink-muted)]" />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      {/* Guaranteed skip-to-content target — even if the downstream
          page's `<main>` lacks an id, this focusable span sits right
          between the top nav and the page body so the skip-link lands
          the user immediately before primary content. WCAG 2.4.1. */}
      <span
        id="main-content"
        tabIndex={-1}
        data-testid="skip-to-content-target"
        className="sr-only"
      >
        Main content
      </span>
    </>
  );
}
